// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewGroupPins.test.ts
//
// How the artwork draws the pin groups (spec dave.savostyanov/0090): which
// number and letter a closed group's pin carries, and opening or closing a
// group. The grouping itself is `./useAskV2ReviewPinView.ts`.
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { clusterCounts, groupMark } from "./pinGroups";
import { type AskV2ReviewPinView } from "./useAskV2ReviewPinView";

type GroupPinsInput = {
  pinView: AskV2ReviewPinView;
  isComparing: boolean;
  commentNumbers: Readonly<Record<string, number>>;
  selectedCommentId: string | null;
  clearComment: () => void;
};

export function useAskV2ReviewGroupPins({
  pinView,
  isComparing,
  commentNumbers,
  selectedCommentId,
  clearComment,
}: GroupPinsInput) {
  const { t } = useTranslation("ui");
  // The artwork follows the grouping only outside a comparison, which draws its own pins.
  const groupedPins = pinView.grouping && !isComparing;
  // The version beside this one hides, folds and numbers its findings the way
  // this review's pin view does.
  const comparedPinView = useMemo(
    () => ({
      enabled: pinView.enabled,
      showMinor: pinView.showMinor,
      grouping: pinView.grouping,
      groupBy: pinView.groupBy,
    }),
    [pinView.enabled, pinView.groupBy, pinView.grouping, pinView.showMinor],
  );
  // A closed group's pin is drawn numbered so it takes the pill path, which prints the group's letter; open, each finding its own number.
  const groupPinNumbers = useMemo(() => {
    if (pinView.openGroup) return commentNumbers;
    const numbers: Record<string, number> = { ...commentNumbers };
    pinView.groups.forEach((group, index) => {
      numbers[group.hub._id] = index + 1;
    });
    return numbers;
  }, [commentNumbers, pinView.groups, pinView.openGroup]);
  // Every closed group, one finding or many, is drawn as a pill with its letter counting its findings to fix and its minor ones.
  const groupPinCluster = useCallback(
    (comment: { _id: string }) => {
      if (!groupedPins || pinView.openGroup) return null;
      const index = pinView.groups.findIndex((g) => g.hub._id === comment._id);
      // Stryker disable next-line ConditionalExpression: `true` is equivalent -- groups[-1] is undefined, so the !group guard returns null anyway; `false` is killed by "draws a closed group's hub as a pill with its letter and counts".
      const group = index >= 0 ? pinView.groups[index] : null;
      if (!group) return null;
      const counts = clusterCounts(group);
      const mark = groupMark(index);
      return {
        ...counts,
        mark,
        label: t("askV2.review.pinCluster.label", {
          mark,
          critical: counts.critical,
          minor: counts.minor,
        }),
      };
    },
    [groupedPins, pinView.groups, pinView.openGroup, t],
  );
  const toggleGroup = useCallback(
    (key: string) => {
      if (pinView.openGroupKey !== key) {
        pinView.setOpenGroupKey(key);
        return;
      }
      // why: the open finding would reopen the group it sits in; closing the group closes it too.
      if (
        // Stryker disable next-line ConditionalExpression: `selectedCommentId != null` -> true is equivalent -- groupOf(null) finds no group, so the second clause is false anyway; the whole-condition true/false replacements are killed by "closing an open group keeps a selection that sits in another group" and "closing the open group that holds the selected finding clears the selection".
        selectedCommentId != null &&
        pinView.groupOf(selectedCommentId)?.key === key
      ) {
        clearComment();
      }
      pinView.setOpenGroupKey(null);
    },
    [clearComment, pinView, selectedCommentId],
  );
  // The group the artwork's bar is open on: only outside a comparison, which draws its own pins.
  const barGroup = groupedPins ? pinView.openGroup : null;

  return {
    groupedPins,
    comparedPinView,
    groupPinNumbers,
    groupPinCluster,
    toggleGroup,
    barGroup,
  };
}

export type AskV2ReviewGroupPins = ReturnType<typeof useAskV2ReviewGroupPins>;
